import { EMULATOR_TRANSCRIPT_OUTCOME_OPTIONS } from "./api";

export type EmulatorTranscriptEntry = {
  id: string;
  text: string;
  createdAt: string;
};

export type EmulatorTranscriptSettings = {
  autoDeliver: boolean;
  delaySeconds: number;
};

export const TRANSCRIPT_HISTORY_KEY = "m360.flexEmulatorTranscriptHistory";
export const TRANSCRIPT_SETTINGS_KEY = "m360.flexEmulatorTranscriptSettings";
export const STAGED_TRANSCRIPT_KEY = "m360.flexEmulatorStagedTranscript";
export const TRANSCRIPT_HISTORY_LIMIT = 10;

export const DEFAULT_TRANSCRIPT_SETTINGS: EmulatorTranscriptSettings = {
  autoDeliver: true,
  delaySeconds: 0
};

/**
 * Realistic Membership Services call transcripts. These seed the local
 * transcript library on first run: each one leans into a distinct call type
 * a real agent would categorize differently.
 */
export const SEED_TRANSCRIPTS: readonly string[] = [
  "Agent: Thanks for calling PGA Member Services, how can I help? Member: Hi, I need to renew my annual dues before the deadline. Agent: I can help with that. I see a balance of 395 dollars. Would you like to pay in full today? Member: Yes please, and email me a receipt.",
  "Member: My weekend scores won't post to my GHIN handicap. Agent: Let me take a look. I see an expired club affiliation blocking the sync. If you re-link your club in the app, the scores should post. Member: That worked, they're showing now. Thanks!",
  "Associate: I want to enter the section championship qualifier. Am I eligible? Agent: Yes, you're eligible. The entry deadline is Friday and the fee is 150 dollars. You can register online. Associate: Great, I'll register tonight.",
  "Member: I'm short on PDR credits this cycle. What counts? Agent: I reviewed your transcript. You attended two seminars that are missing paperwork. Submit the credits through the education portal and I'll email you the link. Member: Got it, I'll submit them today.",
  "Parent: I'm looking for PGA Jr. League 13U registration for my 12-year-old. Agent: The season starts in May. There are two facilities nearby with open teams. I'll share the league director's contact. Parent: Perfect, we'll register this week.",
  "Associate: I'm looking for a first assistant professional role. What help is available? Agent: Career services can help. I'll refer you to the section career consultant for a resume review and share the job board link. The consultant will reach out within two business days.",
  "Member: I need a ruling. I took free relief from casual water on the 7th hole. Was that right? Agent: Yes. You find the nearest point of complete relief and drop within one club-length. The rule is in the app if you want to read it. Member: That settles our debate. Thanks!",
  "Member: I moved to a new state and need to transfer my section affiliation. Agent: You're in good standing, so that's straightforward. I'll submit the transfer request for you. The new section office will confirm within a week.",
  "Member: I'm a veteran asking about PGA HOPE programming. Agent: PGA HOPE runs clinics for disabled veterans. There's a current schedule in your area and you meet the eligibility basics. I'll connect you with the local coordinator. Member: Thank you, I appreciate it.",
  "Member: I'm locked out of my online account and I need to update my email. Agent: I'll verify your identity first. Done. I've updated your contact email. Let's reset the password now. Can you confirm you're logged in? Member: Yes, I'm in. Thanks!"
];

/**
 * Short titles for the seed transcripts, in the same order. The picker shows
 * the title plus a snippet so ten long transcripts stay scannable.
 */
export const SEED_TRANSCRIPT_TITLES: readonly string[] = [
  "Dues renewal",
  "GHIN score sync",
  "Tournament entry",
  "PDR credits",
  "PGA Jr. League signup",
  "Career help",
  "Rules ruling",
  "Section transfer",
  "PGA HOPE",
  "Account recovery"
];

export type EmulatorTranscriptSource = "sample" | "recent";

export const isSeedTranscriptText = (text: string): boolean => SEED_TRANSCRIPTS.includes(text);

export const transcriptSourceForText = (text: string): EmulatorTranscriptSource =>
  isSeedTranscriptText(text) ? "sample" : "recent";

export const transcriptSnippet = (text: string, length = 90): string =>
  text.length > length ? `${text.slice(0, length)}…` : text;

/**
 * Short display title for a transcript. Seeds use their curated titles;
 * custom entries derive one from their opening words so the picker never
 * shows a wall of text as a label.
 */
export const transcriptTitleForText = (text: string): string => {
  const seedIndex = SEED_TRANSCRIPTS.indexOf(text);
  if (seedIndex >= 0) return SEED_TRANSCRIPT_TITLES[seedIndex] ?? `Sample ${seedIndex + 1}`;

  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "Untitled transcript";
  const title = words.slice(0, 6).join(" ");
  return title.length > 48 ? `${title.slice(0, 48).trimEnd()}…` : title;
};

const generateEntryId = (): string => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export const toHistoryEntries = (texts: readonly string[]): EmulatorTranscriptEntry[] =>
  texts.map((text) => ({ id: generateEntryId(), text, createdAt: new Date().toISOString() }));

const readStorageItem = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorageItem = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage unavailable (private mode, SSR); the library lasts for this page only.
  }
};

const isHistoryEntry = (value: unknown): value is EmulatorTranscriptEntry => {
  if (!value || typeof value !== "object") return false;
  const entry = value as Partial<EmulatorTranscriptEntry>;
  return typeof entry.id === "string" && typeof entry.text === "string";
};

/**
 * Most-recent-first transcript history (capped at 10). Seeds from realistic
 * Membership Services calls on first run; an explicitly emptied library stays
 * empty until restored.
 */
export const loadTranscriptHistory = (): EmulatorTranscriptEntry[] => {
  const rawValue = readStorageItem(TRANSCRIPT_HISTORY_KEY);
  if (rawValue === null) {
    const seeded = toHistoryEntries(SEED_TRANSCRIPTS);
    writeStorageItem(TRANSCRIPT_HISTORY_KEY, JSON.stringify(seeded));
    return seeded;
  }

  try {
    const parsed: unknown = JSON.parse(rawValue);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isHistoryEntry).slice(0, TRANSCRIPT_HISTORY_LIMIT);
  } catch {
    return [];
  }
};

export const pushTranscriptHistory = (text: string): EmulatorTranscriptEntry[] => {
  const trimmed = text.trim();
  const current = loadTranscriptHistory().filter((entry) => entry.text !== trimmed);
  const next = trimmed
    ? [{ id: generateEntryId(), text: trimmed, createdAt: new Date().toISOString() }, ...current].slice(
        0,
        TRANSCRIPT_HISTORY_LIMIT
      )
    : current;
  writeStorageItem(TRANSCRIPT_HISTORY_KEY, JSON.stringify(next));
  return next;
};

export const removeTranscriptHistoryEntry = (id: string): EmulatorTranscriptEntry[] => {
  const next = loadTranscriptHistory().filter((entry) => entry.id !== id);
  writeStorageItem(TRANSCRIPT_HISTORY_KEY, JSON.stringify(next));
  return next;
};

export const restoreSeedTranscripts = (): EmulatorTranscriptEntry[] => {
  const seeded = toHistoryEntries(SEED_TRANSCRIPTS);
  writeStorageItem(TRANSCRIPT_HISTORY_KEY, JSON.stringify(seeded));
  return seeded;
};

/** Moves an entry to the top (most-recently-used first) and returns the list. */
export const promoteTranscriptEntry = (id: string): EmulatorTranscriptEntry[] => {
  const current = loadTranscriptHistory();
  const entry = current.find((item) => item.id === id);
  if (!entry) return current;
  const next = [entry, ...current.filter((item) => item.id !== id)];
  writeStorageItem(TRANSCRIPT_HISTORY_KEY, JSON.stringify(next));
  return next;
};

export type StagedTranscript = {
  text: string;
  outcome: string;
};

export const DEFAULT_STAGED_TRANSCRIPT: StagedTranscript = { text: "", outcome: "ready" };

const EMULATOR_TRANSCRIPT_OUTCOMES: readonly string[] = EMULATOR_TRANSCRIPT_OUTCOME_OPTIONS.map(
  (option) => option.value
);

/**
 * The transcript + outcome currently staged to deliver. Persisted so a reload,
 * remount, or hot-update between picking and delivering never loses it.
 */
export const loadStagedTranscript = (): StagedTranscript => {
  const rawValue = readStorageItem(STAGED_TRANSCRIPT_KEY);
  if (!rawValue) return { ...DEFAULT_STAGED_TRANSCRIPT };

  try {
    const parsed = JSON.parse(rawValue) as Partial<StagedTranscript>;
    const outcome = typeof parsed.outcome === "string" ? parsed.outcome : "";
    return {
      text: typeof parsed.text === "string" ? parsed.text : "",
      outcome: (EMULATOR_TRANSCRIPT_OUTCOMES as readonly string[]).includes(outcome)
        ? outcome
        : DEFAULT_STAGED_TRANSCRIPT.outcome
    };
  } catch {
    return { ...DEFAULT_STAGED_TRANSCRIPT };
  }
};

export const saveStagedTranscript = (staged: StagedTranscript): void => {
  writeStorageItem(STAGED_TRANSCRIPT_KEY, JSON.stringify(staged));
};

export const loadTranscriptSettings = (): EmulatorTranscriptSettings => {
  const rawValue = readStorageItem(TRANSCRIPT_SETTINGS_KEY);
  if (!rawValue) return { ...DEFAULT_TRANSCRIPT_SETTINGS };

  try {
    const parsed = JSON.parse(rawValue) as Partial<EmulatorTranscriptSettings>;
    return {
      autoDeliver:
        parsed.autoDeliver === undefined ? DEFAULT_TRANSCRIPT_SETTINGS.autoDeliver : Boolean(parsed.autoDeliver),
      delaySeconds: Math.max(0, Number.parseInt(String(parsed.delaySeconds ?? 0), 10) || 0)
    };
  } catch {
    return { ...DEFAULT_TRANSCRIPT_SETTINGS };
  }
};

export const saveTranscriptSettings = (settings: EmulatorTranscriptSettings): void => {
  writeStorageItem(TRANSCRIPT_SETTINGS_KEY, JSON.stringify(settings));
};
